import React, { useRef } from 'react';
import ReactDOM from 'react-dom';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { Truck, Package, Activity, LogOut, Menu, X, Users, ClipboardCheck, GitCompare, MessageSquare, Loader2, LayoutDashboard, AlertTriangle, Search, Cloud, CloudOff, RefreshCw, Mail, Warehouse, ChevronDown, ChevronRight, BookOpen, ScanLine, User, ShoppingCart, CreditCard, Shield, Gift, FileText, Wrench, Headphones, Bell, AlertOctagon, ClipboardList, Clock, Building2, CalendarClock, Briefcase, BarChart3, HeartPulse, Receipt, PanelLeftClose, PanelLeftOpen, Megaphone, Star, Send, Zap, Phone, Filter, DollarSign, Globe, TrendingUp, MessageCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useUnreadMessages } from '../contexts/UnreadMessagesContext';
import { useSupport } from '../contexts/SupportContext';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { HcpSyncButton } from './HcpSyncButton';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from './ui/dropdown-menu';
import { useState, useEffect } from 'react';
import { feedbackAPI, partRequestsAPI, isPollingPaused, userPreferencesAPI } from '../lib/api';
import { toast } from 'sonner';
import { GlobalSearch } from './GlobalSearch';
import { PendingActionsDrawer } from './PendingActionsDrawer';
import { NotificationsDropdown } from './NotificationsDropdown';
import { ThemeToggle } from './ThemeToggle';
import EmployeeClockControl from './EmployeeClockControl';
import { offlineQueue } from '../lib/offlineQueue';
import { ScanManager } from './ScanManager';
import { Logo } from './Logo';
import { TrialCountdownBanner, BetaSunsetBanner } from './BillingBanners';
import { OnboardingBanners } from './OnboardingBanners';
import { TourRunner } from '../onboarding/TourRunner';
import { ThemeApplier } from './ThemeApplier';
import CommandPalette from './CommandPalette';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from './ui/tooltip';
import { useTranslation } from 'react-i18next';
import { LanguageSwitcher } from './LanguageSwitcher';

/* Flyout submenu for collapsed sidebar sections */
const SidebarFlyout = ({ icon: Icon, label, isActive, items, currentPath, onNavigate, testId }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const triggerRef = React.useRef(null);
  const timeoutRef = React.useRef(null);

  const handleEnter = () => {
    clearTimeout(timeoutRef.current);
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setPos({ top: rect.top, left: rect.right + 4 });
    }
    setOpen(true);
  };
  const handleLeave = () => { timeoutRef.current = setTimeout(() => setOpen(false), 180); };

  return (
    <div className="relative" onMouseEnter={handleEnter} onMouseLeave={handleLeave} ref={triggerRef}>
      <button
        className={`relative flex items-center justify-center w-full h-10 rounded-lg transition-all duration-150 ${
          isActive
            ? 'border border-white/[0.85] bg-transparent text-white'
            : 'border border-transparent text-white/70 hover:bg-white/10 hover:text-white'
        }`}
        data-testid={testId}
        onClick={() => items[0]?.to && onNavigate(items[0].to)}
      >
        <Icon className="w-5 h-5" />
      </button>
      {open && items.length > 0 && ReactDOM.createPortal(
        <div
          className="fixed z-[100] min-w-[180px] rounded-lg border border-border bg-popover shadow-lg py-1"
          style={{ top: pos.top, left: pos.left }}
          onMouseEnter={handleEnter}
          onMouseLeave={handleLeave}
        >
          <div className="px-3 py-1.5 text-[11px] font-semibold text-muted-foreground uppercase tracking-wide">{label}</div>
          {items.map((sub) => {
            const active = currentPath === sub.to || currentPath.startsWith(sub.to + '/');
            return (
              <button
                key={sub.to}
                onClick={() => { onNavigate(sub.to); setOpen(false); }}
                className={`w-full flex items-center gap-2 px-3 py-2 text-sm transition-colors ${
                  active
                    ? 'bg-primary/10 text-primary font-medium'
                    : 'text-popover-foreground hover:bg-muted'
                }`}
                data-testid={`flyout-${sub.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <sub.icon className="w-4 h-4 shrink-0" />
                <span>{sub.labelKey ? t(sub.labelKey) : sub.label}</span>
              </button>
            );
          })}
        </div>,
        document.body
      )}
    </div>
  );
};

// ── Phone Dialer Launcher (top bar) ──
const PhoneDialerButton = () => {
  const [ready, setReady] = React.useState(false);
  const { hasAddon } = useAuth();
  const navigate = useNavigate();
  const addonEnabled = hasAddon && hasAddon('phone_system');

  React.useEffect(() => {
    const handler = (e) => {
      if (e.detail?.ready !== undefined) setReady(e.detail.ready);
    };
    window.addEventListener('softphone:status', handler);
    return () => window.removeEventListener('softphone:status', handler);
  }, []);

  const handleClick = () => {
    if (addonEnabled) {
      window.dispatchEvent(new CustomEvent('softphone:toggle'));
    } else {
      navigate('/billing');
    }
  };

  return (
    <Button
      variant="ghost"
      size="icon"
      className="relative text-foreground hover:bg-muted md:text-white md:hover:bg-white/10 md:hover:text-white"
      onClick={handleClick}
      data-testid="topbar-phone-btn"
      title={addonEnabled ? 'Open Dialer' : 'Phone System — Upgrade'}
    >
      <Phone className="w-5 h-5" />
      {addonEnabled && ready && (
        <span className="absolute top-1 right-1 w-2 h-2 rounded-full bg-emerald-400" />
      )}
      {!addonEnabled && (
        <span className="absolute -top-0.5 -right-0.5 w-3 h-3 rounded-full bg-amber-400 border border-white text-[7px] flex items-center justify-center font-bold text-white">!</span>
      )}
    </Button>
  );
};

export const Layout = ({ children }) => {
  const { user, logout, isOwner, isOffice, isTech, assignedVanId, hasPerm, hasEntitlement, hasAddon, dismissLocaleOnboarding } = useAuth();
  const { t } = useTranslation();
  const { unreadCount: unreadMessagesCount } = useUnreadMessages();
  const { openDrawer: openSupportDrawer } = useSupport();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebar_collapsed');
    return saved === 'true';
  });
  const [dark, setDark] = useState(() => {
    if (typeof window === 'undefined') return false;
    const stored = localStorage.getItem('theme');
    if (stored) return stored === 'dark';
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });
  const [prefsLoaded, setPrefsLoaded] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [feedbackEmail, setFeedbackEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // Initialize inventory expanded state from localStorage, default to collapsed
  const [inventoryExpanded, setInventoryExpanded] = useState(() => {
    const saved = localStorage.getItem('sidebar_inventory_expanded');
    return saved !== null ? JSON.parse(saved) : false; // Default collapsed
  });
  const [scannerOpen, setScannerOpen] = useState(false);
  const [commandPaletteOpen, setCommandPaletteOpen] = useState(false);
  const [activeCallBarVisible, setActiveCallBarVisible] = useState(false);

  // i18n — one-time onboarding toast for users whose locale was auto-detected
  // at signup (e.g. es-MX browser → 'es'). Shows ONCE, then is dismissed both
  // client- and server-side so it never reappears on subsequent logins.
  useEffect(() => {
    if (!user?.locale_onboarding_pending) return;
    const id = setTimeout(() => {
      toast.message(t('localeOnboarding.title'), {
        description: t('localeOnboarding.body'),
        duration: 12000,
        action: {
          label: t('common.ok'),
          onClick: () => { dismissLocaleOnboarding && dismissLocaleOnboarding(); },
        },
        onDismiss: () => { dismissLocaleOnboarding && dismissLocaleOnboarding(); },
        onAutoClose: () => { dismissLocaleOnboarding && dismissLocaleOnboarding(); },
      });
    }, 600);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.locale_onboarding_pending]);

  // Listen for active call bar visibility from Softphone
  useEffect(() => {
    const handler = (e) => setActiveCallBarVisible(e.detail?.active ?? false);
    window.addEventListener('softphone:activecall', handler);
    return () => window.removeEventListener('softphone:activecall', handler);
  }, []);

  // Card-required gate: redirect to /trial-setup if card not yet collected
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    const checkCardRequired = async () => {
      try {
        const { billingAPI } = await import('../lib/api');
        const res = await billingAPI.getStatus();
        if (!cancelled && res.data.card_required_pending) {
          navigate('/trial-setup', { replace: true });
        }
      } catch {
        // Silently fail — don't block app if billing check fails
      }
    };
    checkCardRequired();
    return () => { cancelled = true; };
  }, [user, navigate]);
  
  // Persist inventory expanded state to localStorage
  useEffect(() => {
    localStorage.setItem('sidebar_inventory_expanded', JSON.stringify(inventoryExpanded));
  }, [inventoryExpanded]);
  
  // Offline queue state
  const [queueState, setQueueState] = useState({
    isOnline: navigator.onLine,
    isSyncing: false,
    pendingCount: 0,
    failedCount: 0,
    totalCount: 0,
  });

  useEffect(() => {
    const unsubscribe = offlineQueue.subscribe(setQueueState);
    offlineQueue.getQueueState().then(setQueueState);
    return unsubscribe;
  }, []);

  // Persist sidebar collapsed state to localStorage (fallback)
  useEffect(() => {
    localStorage.setItem('sidebar_collapsed', String(sidebarCollapsed));
  }, [sidebarCollapsed]);

  // Apply dark mode class on <html>
  useEffect(() => {
    const root = document.documentElement;
    if (dark) {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
    localStorage.setItem('theme', dark ? 'dark' : 'light');
  }, [dark]);

  // Fetch user preferences on mount
  useEffect(() => {
    let cancelled = false;
    userPreferencesAPI.get()
      .then(res => {
        if (cancelled) return;
        const { theme_mode, sidebar_collapsed } = res.data;
        setDark(theme_mode === 'dark');
        setSidebarCollapsed(sidebar_collapsed);
        setPrefsLoaded(true);
      })
      .catch(() => { setPrefsLoaded(true); });
    return () => { cancelled = true; };
  }, []);

  // Sync preferences to API (debounced, only after initial load)
  useEffect(() => {
    if (!prefsLoaded) return;
    const timer = setTimeout(() => {
      userPreferencesAPI.update({
        theme_mode: dark ? 'dark' : 'light',
        sidebar_collapsed: sidebarCollapsed,
      }).catch(() => {});
    }, 400);
    return () => clearTimeout(timer);
  }, [dark, sidebarCollapsed, prefsLoaded]);

  // Track whether the user has ever interacted with sidebar sections.
  // On first-ever session, skip auto-expand so new users see a clean collapsed sidebar.
  const sectionsInitialized = useRef(
    localStorage.getItem('sidebar_sections_initialized') === '1'
  );

  // Mark sections as initialized on any manual toggle
  const markSectionsInitialized = () => {
    if (!sectionsInitialized.current) {
      sectionsInitialized.current = true;
      localStorage.setItem('sidebar_sections_initialized', '1');
    }
  };

  // Auto-expand sections when on related routes (skip for brand-new users)
  useEffect(() => {
    if (!sectionsInitialized.current) return;
    const inventoryRoutes = ['/warehouse', '/catalogue', '/replenishment', '/compare', '/exceptions', '/quarantine', '/import-review'];
    if (inventoryRoutes.some(route => location.pathname.startsWith(route))) {
      setInventoryExpanded(true);
    }
    
    // Auto-expand customers section
    const customersRoutes = ['/customers', '/sales-pipeline', '/estimates', '/jobs', '/invoices', '/maintenance-plans'];
    if (customersRoutes.some(route => location.pathname.startsWith(route))) {
      setCustomersExpanded(true);
    }
    
    // Auto-expand operations section when on operations-related routes
    const operationsRoutes = ['/part-requests', '/approvals', '/job-kits', '/job-templates', '/forms', '/job-timeline', '/truck-prep', '/weekly-truck-report'];
    if (operationsRoutes.some(route => location.pathname.startsWith(route))) {
      setOperationsExpanded(true);
    }
    
    // Auto-expand fleet section when on fleet-related routes
    const fleetRoutes = ['/vans', '/tools', '/fleet-health'];
    if (fleetRoutes.some(route => location.pathname.startsWith(route))) {
      setFleetExpanded(true);
    }
    
    // Auto-expand reports section
    const reportsRoutes = ['/reports', '/scheduled-reports', '/inventory/activity', '/usage-report', '/technician-scorecards'];
    if (reportsRoutes.some(route => location.pathname.startsWith(route))) {
      setReportsExpanded(true);
    }
    
    // Auto-expand purchasing section when on purchasing-related routes
    const purchasingRoutes = ['/purchase-orders', '/vendors'];
    if (purchasingRoutes.some(route => location.pathname.startsWith(route))) {
      setPurchasingExpanded(true);
    }
  }, [location.pathname]);

  // Cmd+K / Ctrl+K global shortcut for Command Palette
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setCommandPaletteOpen(prev => !prev);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  // Define inventory sub-items (includes tools section)
  const inventorySubItems = [
    { to: '/catalogue', icon: BookOpen, label: 'Parts List', labelKey: 'nav.catalogue' },
    { to: '/warehouse', icon: Warehouse, label: 'Warehouses', labelKey: 'nav.warehouses' },
    { to: '/replenishment', icon: Package, label: 'Replenishment' },
    { to: '/compare', icon: GitCompare, label: 'Compare Vehicles' },
    { type: 'separator', label: 'Tools' },
    { to: '/exceptions', icon: AlertOctagon, label: 'Exceptions' },
    { to: '/quarantine', icon: AlertTriangle, label: 'Quarantine' },
  ];

  // Define customers sub-items (replaces sales section)
  const [customersExpanded, setCustomersExpanded] = useState(() => {
    const saved = localStorage.getItem('sidebar_customers_expanded');
    return saved !== null ? JSON.parse(saved) : false;
  });
  useEffect(() => {
    localStorage.setItem('sidebar_customers_expanded', JSON.stringify(customersExpanded));
  }, [customersExpanded]);

  const customersSubItems = [
    { to: '/sales-pipeline', icon: Filter, label: 'Sales Pipeline' },
    { to: '/estimates', icon: FileText, label: 'Estimates', labelKey: 'nav.estimates' },
    { to: '/jobs', icon: Briefcase, label: 'Jobs', labelKey: 'nav.jobs' },
    { to: '/invoices', icon: Receipt, label: 'Invoices', labelKey: 'nav.invoices' },
    { to: '/maintenance-plans', icon: Shield, label: 'Plans' },
  ];
  
  // Define fleet sub-items
  const [fleetExpanded, setFleetExpanded] = useState(() => {
    const saved = localStorage.getItem('sidebar_fleet_expanded');
    return saved !== null ? JSON.parse(saved) : false;
  });
  
  // Persist fleet expanded state
  useEffect(() => {
    localStorage.setItem('sidebar_fleet_expanded', JSON.stringify(fleetExpanded));
  }, [fleetExpanded]);
  
  const fleetSubItems = [
    { to: '/fleet-health', icon: HeartPulse, label: 'Fleet Health' },
    { to: '/tools', icon: Wrench, label: 'Tool Checkout' },
  ];
  
  // Phase 5A: Pending part requests count for notification badge
  const [pendingPartRequestsCount, setPendingPartRequestsCount] = useState(0);
  
  useEffect(() => {
    // Only fetch for office roles
    if (!isOwner && !isOffice) return;
    
    const fetchPendingCount = async () => {
      if (isPollingPaused()) return;
      try {
        const response = await partRequestsAPI.getPendingCount();
        setPendingPartRequestsCount(response.data.pending_count || 0);
      } catch (error) {
        // Silent fail when polling — non-critical
      }
    };
    
    fetchPendingCount();
    // Refresh every 30 seconds
    const interval = setInterval(fetchPendingCount, 30000);
    return () => clearInterval(interval);
  }, [isOwner, isOffice]);
  
  // Define operations sub-items (new grouping)
  const [operationsExpanded, setOperationsExpanded] = useState(() => {
    const saved = localStorage.getItem('sidebar_operations_expanded');
    return saved !== null ? JSON.parse(saved) : false;
  });
  
  // Persist operations expanded state
  useEffect(() => {
    localStorage.setItem('sidebar_operations_expanded', JSON.stringify(operationsExpanded));
  }, [operationsExpanded]);
  
  const operationsSubItems = [
    { to: '/part-requests', icon: ClipboardList, label: 'Part Requests', badge: pendingPartRequestsCount }, // Phase 5A
    { to: '/job-kits', icon: Package, label: 'Job Kits' }, // Phase 6C
    { to: '/job-templates', icon: FileText, label: 'Job Templates' }, // Stage 12
    { to: '/forms', icon: ClipboardList, label: 'Form Templates' }, // Custom Forms V1
    { to: '/truck-prep', icon: Truck, label: 'Truck Prep' }, // Stage 18
    { to: '/weekly-truck-report', icon: BarChart3, label: 'Weekly Truck Report' }, // Stage 23
    { to: '/job-timeline', icon: Clock, label: 'Job Timeline' }, // Phase 5C
    { to: '/approvals', icon: ClipboardCheck, label: 'Approvals' },
  ];

  // Define reports sub-items (split from operations)
  const [reportsExpanded, setReportsExpanded] = useState(() => {
    const saved = localStorage.getItem('sidebar_reports_expanded');
    return saved !== null ? JSON.parse(saved) : false;
  });
  
  // Persist reports expanded state
  useEffect(() => {
    localStorage.setItem('sidebar_reports_expanded', JSON.stringify(reportsExpanded));
  }, [reportsExpanded]);
  
  const reportsSubItems = [
    { to: '/technician-scorecards', icon: Users, label: 'Scorecards', permission: 'reports.view' },
    { to: '/reports/job-costing', icon: BarChart3, label: 'Job Costing', permission: 'reports.view' },
    { to: '/reports/commissions', icon: DollarSign, label: 'Commissions', permission: 'reports.view' },
    { to: '/reports/project-financials', icon: TrendingUp, label: 'Project Financials', permission: 'reports.view' },
    { to: '/scheduled-reports', icon: CalendarClock, label: 'Scheduled Reports', permission: 'reports.view' },
    { to: '/inventory/activity', icon: Activity, label: 'Activity', labelKey: 'nav.activity', permission: 'activity.view' },
    { to: '/usage-report', icon: BarChart3, label: 'Usage Report' },
  ];
  
  // Define marketing section
  const [marketingExpanded, setMarketingExpanded] = useState(() => {
    const saved = localStorage.getItem('sidebar_marketing_expanded');
    return saved !== null ? JSON.parse(saved) : false;
  });
  
  useEffect(() => {
    localStorage.setItem('sidebar_marketing_expanded', JSON.stringify(marketingExpanded));
  }, [marketingExpanded]);
  
  const marketingSubItems = [];
  // Reviews & Website are always available (inventory baseline)
  marketingSubItems.push({ to: '/marketing/reviews', icon: Star, label: 'Reviews' });
  if (hasEntitlement('crm')) {
    marketingSubItems.push({ to: '/marketing/campaigns', icon: Send, label: 'Campaigns' });
    marketingSubItems.push({ to: '/marketing/audiences', icon: Users, label: 'Audiences' });
    marketingSubItems.push({ to: '/marketing/automations', icon: Zap, label: 'Automations' });
    marketingSubItems.push({ to: '/marketing/lead-report', icon: BarChart3, label: 'Lead Report' });
  }
  marketingSubItems.push({ to: '/marketing/website', icon: Globe, label: 'Website' });
  marketingSubItems.push({ to: '/marketing/website-chat', icon: MessageCircle, label: 'Website Chat' });
  
  // Define purchasing section (Vendors only - PO list accessed via parent click)
  const [purchasingExpanded, setPurchasingExpanded] = useState(() => {
    const saved = localStorage.getItem('sidebar_purchasing_expanded');
    return saved !== null ? JSON.parse(saved) : false;
  });
  
  // Persist purchasing expanded state
  useEffect(() => {
    localStorage.setItem('sidebar_purchasing_expanded', JSON.stringify(purchasingExpanded));
  }, [purchasingExpanded]);
  
  const purchasingSubItems = [
    { to: '/vendors', icon: Building2, label: 'Vendors', labelKey: 'nav.vendors' },
  ];

  // Expose nav section expand API for guided tour engine
  useEffect(() => {
    const expandMap = {
      customers: setCustomersExpanded,
      inventory: setInventoryExpanded,
      fleet: setFleetExpanded,
      operations: setOperationsExpanded,
      reports: setReportsExpanded,
      purchasing: setPurchasingExpanded,
      marketing: setMarketingExpanded,
    };
    window.__tourExpandNav = (sectionId) => {
      const setter = expandMap[sectionId];
      if (setter) setter(true);
    };
    return () => { delete window.__tourExpandNav; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const navItems = [];
  
  if (isOwner) {
    navItems.push(
      { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard', labelKey: 'nav.dashboard' },
    );
    if (hasEntitlement('crm')) {
      navItems.push(
        { type: 'customers-section' },
      );
    } else if (hasEntitlement('hcp_jobs')) {
      navItems.push({ to: '/jobs', icon: Briefcase, label: 'Jobs (HCP)', labelKey: 'nav.jobs', badge: 'HCP' });
      navItems.push({ to: '/hcp-deductions', icon: GitCompare, label: 'Material Deductions' });
    }
    if (hasEntitlement('crm')) {
      navItems.push({ to: '/dispatch', icon: CalendarClock, label: 'Dispatch', labelKey: 'nav.dispatch' });
    }
    if (hasEntitlement('crm')) {
      navItems.push({ to: '/price-book', icon: BookOpen, label: 'Price Book', labelKey: 'nav.priceBook' });
    } else if (hasEntitlement('hcp_jobs')) {
      navItems.push({ to: '/price-book', icon: BookOpen, label: 'Price Book', labelKey: 'nav.priceBook' });
    }
    navItems.push(
      { type: 'inventory-section' },
      { type: 'fleet-section' },
      { type: 'purchasing-section' },
      { type: 'operations-section' },
      { type: 'reports-section' },
    );
    navItems.push({ type: 'marketing-section' });
    if (hasAddon('phone_system')) {
      navItems.push({ to: '/contact-center', icon: Phone, label: 'Contact Center', labelKey: 'nav.contactCenter' });
    }
    navItems.push(
      { to: '/messages', icon: MessageSquare, label: 'Messages', labelKey: 'nav.messages' },
    );
  } else if (isOffice) {
    // Office Staff navigation - permission-based
    navItems.push(
      { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard', labelKey: 'nav.dashboard' },
    );
    if (hasEntitlement('crm')) {
      navItems.push(
        { type: 'customers-section' },
      );
    } else if (hasEntitlement('hcp_jobs')) {
      navItems.push({ to: '/jobs', icon: Briefcase, label: 'Jobs (HCP)', labelKey: 'nav.jobs', badge: 'HCP' });
      navItems.push({ to: '/hcp-deductions', icon: GitCompare, label: 'Material Deductions' });
    }
    if (hasEntitlement('crm')) {
      navItems.push({ to: '/dispatch', icon: CalendarClock, label: 'Dispatch', labelKey: 'nav.dispatch' });
    }
    if (hasEntitlement('crm')) {
      navItems.push({ to: '/price-book', icon: BookOpen, label: 'Price Book', labelKey: 'nav.priceBook' });
    } else if (hasEntitlement('hcp_jobs')) {
      navItems.push({ to: '/price-book', icon: BookOpen, label: 'Price Book', labelKey: 'nav.priceBook' });
    }
    navItems.push(
      { type: 'inventory-section' },
      { type: 'fleet-section' },
      ...(hasPerm('po.view') ? [{ type: 'purchasing-section' }] : []),
      { type: 'operations-section' },
      { type: 'reports-section' },
    );
    navItems.push({ type: 'marketing-section' });
    if (hasAddon('phone_system')) {
      navItems.push({ to: '/contact-center', icon: Phone, label: 'Contact Center', labelKey: 'nav.contactCenter' });
    }
    navItems.push(
      { to: '/messages', icon: MessageSquare, label: 'Messages', labelKey: 'nav.messages' },
    );
  } else if (isTech) {
    navItems.push(
      { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard', labelKey: 'nav.dashboard' },
      { type: 'inventory-section' }, // Placeholder for inventory section (limited)
      { type: 'fleet-section' }, // Fleet section for techs
      ...(hasPerm('po.view') ? [{ to: '/purchase-orders', icon: ShoppingCart, label: 'POs', labelKey: 'nav.pos' }] : []) // Techs don't need Vendors submenu
    );
    // Show My Vehicle for tech if assigned
    if (assignedVanId) {
      navItems.push({ to: `/vans/${assignedVanId}`, icon: Truck, label: 'My Vehicle', labelKey: 'nav.myVehicle' });
    }
    navItems.push(
      { type: 'operations-section' },
      { to: '/messages', icon: MessageSquare, label: 'Messages', labelKey: 'nav.messages' }
    );
  }

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleFeedbackSubmit = async (e) => {
    e.preventDefault();
    if (!feedbackMessage.trim()) return;
    
    setSubmitting(true);
    try {
      await feedbackAPI.submit({
        message: feedbackMessage.trim(),
        contact_email: feedbackEmail.trim() || null,
        context: {
          route: location.pathname,
          role: isOwner ? 'owner' : 'tech',
          user_agent: navigator.userAgent
        }
      });
      toast.success('Thank you for your feedback!');
      setFeedbackMessage('');
      setFeedbackEmail('');
      setFeedbackOpen(false);
    } catch (error) {
      toast.error('Failed to submit feedback');
    } finally {
      setSubmitting(false);
    }
  };

  const openFeedbackModal = () => {
    setFeedbackEmail(user?.email || '');
    setFeedbackOpen(true);
  };

  return (
    <div className="min-h-screen bg-background">
      <ThemeApplier />
      <CommandPalette isOpen={commandPaletteOpen} onClose={() => setCommandPaletteOpen(false)} />
      {/* Full-Width Desktop Header - spans entire page width */}
      <header className="hidden md:flex fixed top-0 left-0 right-0 z-50 items-center justify-between h-14 px-6 shadow-[0_2px_6px_rgba(0,0,0,0.15)]" style={{ backgroundColor: 'var(--topbar-bg)' }}>
        {/* Left side - Logo + Sidebar toggle + Search */}
        <div className="flex items-center gap-3 flex-1">
          <Logo size="header" data-testid="header-logo" />
          <button
            onClick={() => setSidebarCollapsed(c => !c)}
            className="p-1.5 rounded-md text-white/60 hover:text-white hover:bg-white/10 transition-colors flex-shrink-0"
            data-testid="sidebar-collapse-toggle"
            aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {sidebarCollapsed ? <PanelLeftOpen className="w-4 h-4" /> : <PanelLeftClose className="w-4 h-4" />}
          </button>
          <div className="relative max-w-md w-full">
            <GlobalSearch dark />
            {/* Cmd+K hint overlay */}
            <button
              onClick={() => setCommandPaletteOpen(true)}
              className="absolute right-2 top-1/2 -translate-y-1/2 hidden lg:flex items-center gap-0.5 rounded border border-white/20 bg-white/10 px-1.5 py-0.5 text-[10px] font-medium text-white/60 hover:bg-white/20 hover:text-white/80 transition-colors"
              data-testid="command-palette-trigger-desktop"
            >
              <span>⌘</span><span>K</span>
            </button>
          </div>
        </div>
        
        {/* Right side - Status, Messages, Notifications, User */}
        <div className="flex items-center gap-2">
          {/* Offline Status Indicator */}
          <PendingActionsDrawer
            trigger={
              <Button variant="ghost" size="sm" className="gap-2 text-white hover:bg-white/10 hover:text-white" data-testid="offline-status-btn">
                {queueState.isSyncing ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin text-white" />
                    <span className="text-xs text-white/70">Syncing...</span>
                  </>
                ) : !queueState.isOnline ? (
                  <>
                    <CloudOff className="w-4 h-4 text-destructive" />
                    <span className="text-xs text-destructive">Offline</span>
                  </>
                ) : queueState.totalCount > 0 ? (
                  <>
                    <Cloud className="w-4 h-4 text-white" />
                    <Badge variant="secondary" className="text-xs h-5">
                      {queueState.totalCount} pending
                    </Badge>
                  </>
                ) : (
                  <>
                    <Cloud className="w-4 h-4 text-green-400" />
                    <span className="text-xs text-white/70">Online</span>
                  </>
                )}
              </Button>
            }
          />
          
          {/* Messages Icon */}
          <Button 
            variant="ghost" 
            size="icon" 
            className="relative text-white hover:bg-white/10 hover:text-white"
            onClick={() => navigate('/messages')}
            data-testid="messages-envelope"
          >
            <Mail className="w-5 h-5" />
            {unreadMessagesCount > 0 && (
              <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] bg-destructive text-[10px] font-bold text-white rounded-full flex items-center justify-center px-1">
                {unreadMessagesCount > 99 ? '99+' : unreadMessagesCount}
              </span>
            )}
          </Button>
          
          {/* Phone Dialer Launcher */}
          {hasAddon && hasAddon('phone_system') && (
            <PhoneDialerButton />
          )}

          {/* HCP Sync Shortcut */}
          <HcpSyncButton />

          {/* Employee Time Clock */}
          <EmployeeClockControl />
          
          {/* Notifications Bell */}
          <NotificationsDropdown />
          
          {/* Theme Toggle */}
          <ThemeToggle dark={dark} onToggle={() => setDark(d => !d)} />

          {/* Language Switcher (top-right) */}
          <LanguageSwitcher testIdPrefix="lang-topnav" />

          {/* User Dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="gap-2 ml-2 text-white hover:bg-white/10 hover:text-white" data-testid="user-dropdown-trigger">
                <User className="w-4 h-4" />
                <span className="text-sm">{user?.name}</span>
                <ChevronDown className="w-3 h-3 text-white/70" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <div className="px-3 py-2">
                <p className="text-sm font-semibold">{user?.name}</p>
                <p className="text-xs text-muted-foreground">{user?.email}</p>
              </div>
              <DropdownMenuSeparator />
              {/* Profile */}
              <DropdownMenuItem onClick={() => navigate('/profile')}>
                <User className="w-4 h-4 mr-2" />
                {t('common.profile')}
              </DropdownMenuItem>
              {/* Referrals */}
              {hasPerm('referrals.view_own') && (
                <DropdownMenuItem onClick={() => navigate('/referrals')}>
                  <Gift className="w-4 h-4 mr-2" />
                  Referrals
                </DropdownMenuItem>
              )}
              {/* Manage Team - moved from sidebar */}
              {(isOwner || isOffice) && (
                <DropdownMenuItem onClick={() => navigate('/team')}>
                  <Users className="w-4 h-4 mr-2" />
                  Manage Team
                </DropdownMenuItem>
              )}
              {/* Billing */}
              {hasPerm('billing.view') && (
                <DropdownMenuItem onClick={() => navigate('/billing')}>
                  <CreditCard className="w-4 h-4 mr-2" />
                  Billing
                </DropdownMenuItem>
              )}
              {/* Company Settings - Owner or branding permission */}
              {(isOwner || hasPerm('settings.branding_edit')) && (
                <DropdownMenuItem onClick={() => navigate('/company-settings')}>
                  <Shield className="w-4 h-4 mr-2" />
                  Company Settings
                </DropdownMenuItem>
              )}
              {/* Phone Numbers - Owner/Office with Phone addon */}
              {(isOwner || isOffice) && hasAddon && hasAddon('phone_system') && (
                <DropdownMenuItem onClick={() => navigate('/settings/phone-numbers')}>
                  <Phone className="w-4 h-4 mr-2" />
                  Phone Numbers
                </DropdownMenuItem>
              )}
              {/* Communications Settings */}
              {(isOwner || isOffice) && hasAddon && hasAddon('phone_system') && (
                <DropdownMenuItem onClick={() => navigate('/settings/communications')}>
                  <Headphones className="w-4 h-4 mr-2" />
                  Communications
                </DropdownMenuItem>
              )}
              {/* Custom Fields Settings */}
              {(isOwner || isOffice) && (
                <DropdownMenuItem onClick={() => navigate('/settings/custom-fields')} data-testid="custom-fields-settings-menu">
                  <Wrench className="w-4 h-4 mr-2" />
                  Custom Fields
                </DropdownMenuItem>
              )}
              {/* Workflows Settings */}
              {(isOwner || isOffice) && (
                <DropdownMenuItem onClick={() => navigate('/settings/workflows')} data-testid="workflows-settings-menu">
                  <Zap className="w-4 h-4 mr-2" />
                  Workflows
                </DropdownMenuItem>
              )}
              {/* Job Costing Settings */}
              {(isOwner || isOffice) && (
                <DropdownMenuItem onClick={() => navigate('/settings/job-costing')} data-testid="job-costing-settings-menu">
                  <DollarSign className="w-4 h-4 mr-2" />
                  Job Costing
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={openSupportDrawer} data-testid="support-menu-item">
                <Headphones className="w-4 h-4 mr-2" />
                Support
              </DropdownMenuItem>
              <DropdownMenuItem onClick={openFeedbackModal}>
                <MessageSquare className="w-4 h-4 mr-2" />
                Send Feedback
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleLogout} className="text-destructive focus:text-destructive">
                <LogOut className="w-4 h-4 mr-2" />
                {t('auth.logout')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      {/* Desktop Sidebar - below the header */}
      <aside
        className={`hidden md:flex md:fixed md:top-14 md:left-0 md:z-40 md:flex-col border-r border-border/30 transition-[width,bottom] duration-200 ease-in-out ${
          sidebarCollapsed ? 'md:w-16' : 'md:w-64'
        } ${activeCallBarVisible ? 'md:bottom-14' : 'md:bottom-0'}`}
        data-testid="desktop-sidebar"
      >
        <div className="flex flex-col h-full sidebar-gradient overflow-hidden">
          {/* Navigation - scrollable */}
          <TooltipProvider delayDuration={120}>
          <nav className={`flex-1 min-h-0 pt-3 pb-3 space-y-1 overflow-y-auto scrollbar-thin ${sidebarCollapsed ? 'px-2' : 'px-4'}`} style={{ scrollbarGutter: 'stable' }}>
            {navItems.map((item, index) => {
              /* ───────────────── SECTION ITEMS ───────────────── */
              const sectionConfigs = {
                'customers-section': { key: 'customers', icon: Users, label: t('nav.customers'), expanded: customersExpanded, toggle: () => { markSectionsInitialized(); setCustomersExpanded(p => !p); }, items: customersSubItems, isActive: location.pathname.startsWith('/customers') || customersSubItems.some(sub => location.pathname.startsWith(sub.to)), navigateTo: '/customers' },
                'inventory-section': { key: 'inventory', icon: Package, label: t('nav.inventory'), expanded: inventoryExpanded, toggle: () => { markSectionsInitialized(); setInventoryExpanded(p => !p); }, items: inventorySubItems, isActive: inventorySubItems.filter(s => s.to).some(sub => location.pathname.startsWith(sub.to)), filterFn: (sub) => {
                  if (sub.type === 'separator') return true;
                  return !(isTech && sub.to !== '/warehouse');
                }},
                'fleet-section': { key: 'fleet', icon: Truck, label: t('nav.fleet'), expanded: fleetExpanded, toggle: () => { markSectionsInitialized(); setFleetExpanded(p => !p); }, items: [
                  ...((isOwner || isOffice) ? [{ to: '/vans', icon: Truck, label: t('nav.vehicles') }] : []),
                  ...fleetSubItems
                ], isActive: location.pathname.startsWith('/vans') || fleetSubItems.some(sub => location.pathname.startsWith(sub.to)) },
                'operations-section': { key: 'operations', icon: ClipboardCheck, label: t('nav.operations'), expanded: operationsExpanded, toggle: () => { markSectionsInitialized(); setOperationsExpanded(p => !p); }, items: operationsSubItems, isActive: operationsSubItems.some(sub => location.pathname.startsWith(sub.to)), filterFn: (sub) => {
                  if (sub.permission && !hasPerm(sub.permission)) return false;
                  if (isTech && sub.to !== '/inventory/activity' && sub.to !== '/part-requests') return false;
                  return true;
                }},
                'purchasing-section': { key: 'purchasing', icon: ShoppingCart, label: t('nav.pos'), expanded: purchasingExpanded, toggle: () => { markSectionsInitialized(); setPurchasingExpanded(p => !p); }, items: purchasingSubItems, isActive: location.pathname.startsWith('/purchase-orders') || purchasingSubItems.some(sub => location.pathname.startsWith(sub.to)), navigateTo: '/purchase-orders' },
                'reports-section': { key: 'reports', icon: BarChart3, label: t('nav.reports'), expanded: reportsExpanded, toggle: () => { markSectionsInitialized(); setReportsExpanded(p => !p); }, items: reportsSubItems, isActive: location.pathname.startsWith('/reports') || reportsSubItems.some(sub => location.pathname.startsWith(sub.to)), navigateTo: '/reports', filterFn: (sub) => {
                  if (sub.permission && !hasPerm(sub.permission)) return false;
                  return true;
                }},
                'marketing-section': { key: 'marketing', icon: Megaphone, label: t('nav.marketing'), expanded: marketingExpanded, toggle: () => { markSectionsInitialized(); setMarketingExpanded(p => !p); }, items: marketingSubItems, isActive: location.pathname.startsWith('/marketing'), navigateTo: '/marketing/reviews' },
              };

              const cfg = sectionConfigs[item.type];
              if (cfg) {
                const visibleItems = cfg.filterFn ? cfg.items.filter(cfg.filterFn) : cfg.items;
                // Filter out separators for flyout (collapsed sidebar)
                const flyoutItems = visibleItems.filter(s => s.to);

                /* ── COLLAPSED: icon + flyout ── */
                if (sidebarCollapsed) {
                  return (
                    <SidebarFlyout
                      key={cfg.key}
                      icon={cfg.icon}
                      label={cfg.label}
                      isActive={cfg.isActive}
                      items={cfg.navigateTo ? [{ to: cfg.navigateTo, icon: cfg.icon, label: cfg.label }, ...flyoutItems] : flyoutItems}
                      currentPath={location.pathname}
                      onNavigate={(to) => navigate(to)}
                      testId={`nav-${cfg.key}`}
                    />
                  );
                }

                /* ── EXPANDED: normal section ── */
                /* Parent row: clicking icon+label navigates (if navigateTo set) + expands; clicking chevron only toggles */
                const handleParentClick = () => {
                  if (cfg.navigateTo) {
                    navigate(cfg.navigateTo);
                    if (!cfg.expanded) cfg.toggle();
                  } else {
                    cfg.toggle();
                  }
                };
                const handleChevronClick = (e) => {
                  e.stopPropagation();
                  cfg.toggle();
                };

                return (
                  <div key={`${cfg.key}-section`}>
                    <button
                      onClick={handleParentClick}
                      className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg font-medium transition-all duration-150 ${
                        cfg.isActive && !cfg.expanded
                          ? 'border border-white/[0.85] bg-transparent text-white'
                          : 'border border-transparent text-white/70 hover:bg-white/10 hover:text-white'
                      }`}
                      data-testid={`nav-${cfg.key}`}
                    >
                      <cfg.icon className="w-5 h-5" />
                      <span className="flex-1 text-left">{cfg.label}</span>
                      <span
                        onClick={handleChevronClick}
                        className="p-1 -m-1 rounded hover:bg-white/10 transition-colors"
                        data-testid={`nav-${cfg.key}-chevron`}
                      >
                        {cfg.expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                      </span>
                    </button>
                    {cfg.expanded && (
                      <div className="ml-4 mt-1 space-y-1">
                        {visibleItems.map((subItem, subIdx) => {
                          // Render separator
                          if (subItem.type === 'separator') {
                            return (
                              <div key={`sep-${subIdx}`} className="px-4 pt-2 pb-1">
                                <span className="text-[10px] uppercase tracking-wider text-white/40 font-semibold">{subItem.label}</span>
                              </div>
                            );
                          }
                          return (
                            <NavLink
                              key={subItem.to}
                              to={subItem.to}
                              className={({ isActive }) =>
                                `flex items-center gap-3 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-150 ${
                                  isActive
                                    ? 'border border-white/[0.85] bg-transparent text-white'
                                    : 'border border-transparent text-white/60 hover:bg-white/10 hover:text-white'
                                }`
                              }
                              data-testid={`nav-${subItem.label.toLowerCase().replace(/\s+/g, '-')}`}
                            >
                              <subItem.icon className="w-4 h-4" />
                              <span className="flex-1">{subItem.labelKey ? t(subItem.labelKey) : subItem.label}</span>
                              {subItem.badge > 0 && (
                                <Badge variant="secondary" className="text-xs h-5 min-w-[20px] justify-center">
                                  {subItem.badge > 99 ? '99+' : subItem.badge}
                                </Badge>
                              )}
                            </NavLink>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              }

              // Skip unknown section types
              if (item.type) return null;

              // Check permission
              if (item.permission && !hasPerm(item.permission)) return null;
              if (!item.icon || !item.to) return null;

              /* ───────────────── REGULAR ITEMS ───────────────── */
              const isMessagesItem = item.label === 'Messages';
              const badgeCount = isMessagesItem ? unreadMessagesCount : 0;
              const displayBadge = badgeCount > 99 ? '99+' : badgeCount;
              const isActive = location.pathname === item.to || location.pathname.startsWith(item.to + '/');

              if (sidebarCollapsed) {
                return (
                  <Tooltip key={item.to}>
                    <TooltipTrigger asChild>
                      <NavLink
                        to={item.to}
                        className={({ isActive: navActive }) =>
                          `relative flex items-center justify-center w-full h-10 rounded-lg transition-all duration-150 ${
                            navActive
                              ? 'border border-white/[0.85] bg-transparent text-white'
                              : 'border border-transparent text-white/70 hover:bg-white/10 hover:text-white'
                          }`
                        }
                        data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                      >
                        <item.icon className="w-5 h-5" />
                        {badgeCount > 0 && (
                          <span className="absolute -top-1 -right-1 min-w-[16px] h-[16px] bg-destructive text-[9px] font-bold text-white rounded-full flex items-center justify-center px-0.5">
                            {displayBadge}
                          </span>
                        )}
                      </NavLink>
                    </TooltipTrigger>
                    <TooltipContent side="right" className="font-medium">
                      {item.labelKey ? t(item.labelKey) : item.label}
                    </TooltipContent>
                  </Tooltip>
                );
              }

              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={({ isActive: navActive }) =>
                    `flex items-center gap-3 px-4 py-3 rounded-lg font-medium transition-all duration-150 ${
                      navActive
                        ? 'border border-white/[0.85] bg-transparent text-white'
                        : 'border border-transparent text-white/70 hover:bg-white/10 hover:text-white'
                    }`
                  }
                  data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                >
                  <item.icon className="w-5 h-5" />
                  <span className="flex-1">{item.labelKey ? t(item.labelKey) : item.label}</span>
                  {item.badge && (
                    <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300 border border-amber-500/30">
                      {item.badge}
                    </span>
                  )}
                  {badgeCount > 0 && (
                    <Badge 
                      variant="destructive" 
                      className="text-[10px] h-5 min-w-[20px] px-1.5 flex items-center justify-center"
                    >
                      {displayBadge}
                    </Badge>
                  )}
                </NavLink>
              );
            })}
          </nav>
          </TooltipProvider>

          {/* Clean bottom spacing */}
          <div className="flex-shrink-0 h-3" />
        </div>
      </aside>

      {/* Mobile Header */}
      <header className="md:hidden fixed top-0 left-0 right-0 z-50 h-14 bg-background border-b border-border flex items-center justify-between px-4">
        <Logo size="mobile-header" data-testid="mobile-header-logo" />
        <div className="flex items-center gap-1">
          {/* Mobile Command Palette trigger */}
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setCommandPaletteOpen(true)}
            data-testid="command-palette-trigger-mobile"
          >
            <Search className="w-5 h-5" />
          </Button>
          {/* Phone Dialer (mobile) */}
          {hasAddon && hasAddon('phone_system') && (
            <PhoneDialerButton />
          )}
          {/* Theme Toggle */}
          <ThemeToggle variant="default" dark={dark} onToggle={() => setDark(d => !d)} />
          {/* Mobile User Dropdown - Consolidated actions */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="relative" data-testid="mobile-user-dropdown-trigger">
                <User className="w-5 h-5" />
                {/* Show badge if unread messages or notifications */}
                {unreadMessagesCount > 0 && (
                  <span className="absolute -top-1 -right-1 min-w-[14px] h-[14px] bg-destructive text-[8px] font-bold text-white rounded-full flex items-center justify-center px-0.5">
                    {unreadMessagesCount > 9 ? '9+' : unreadMessagesCount}
                  </span>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <div className="px-3 py-2">
                <p className="text-sm font-semibold">{user?.name}</p>
                <p className="text-xs text-muted-foreground">{user?.email}</p>
              </div>
              <DropdownMenuSeparator />
              {/* Messages link */}
              <DropdownMenuItem onClick={() => navigate('/messages')} className="relative">
                <Mail className="w-4 h-4 mr-2" />
                Messages
                {unreadMessagesCount > 0 && (
                  <span className="ml-auto bg-destructive text-[10px] font-bold text-white rounded-full px-1.5 py-0.5">
                    {unreadMessagesCount > 99 ? '99+' : unreadMessagesCount}
                  </span>
                )}
              </DropdownMenuItem>
              {/* Scan option */}
              <DropdownMenuItem onClick={() => setScannerOpen(true)}>
                <ScanLine className="w-4 h-4 mr-2" />
                Scan Item
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              {/* Profile */}
              <DropdownMenuItem onClick={() => navigate('/profile')}>
                <User className="w-4 h-4 mr-2" />
                {t('common.profile')}
              </DropdownMenuItem>
              {/* Referrals - shown if user has referrals.view_own permission */}
              {hasPerm('referrals.view_own') && (
                <DropdownMenuItem onClick={() => navigate('/referrals')}>
                  <Gift className="w-4 h-4 mr-2" />
                  Referrals
                </DropdownMenuItem>
              )}
              {/* Manage Team - moved from sidebar */}
              {(isOwner || isOffice) && (
                <DropdownMenuItem onClick={() => navigate('/team')}>
                  <Users className="w-4 h-4 mr-2" />
                  Manage Team
                </DropdownMenuItem>
              )}
              {/* Billing - shown if user has billing.view permission */}
              {hasPerm('billing.view') && (
                <DropdownMenuItem onClick={() => navigate('/billing')}>
                  <CreditCard className="w-4 h-4 mr-2" />
                  Billing
                </DropdownMenuItem>
              )}
              {/* Phone Numbers - Owner/Office with Phone addon */}
              {(isOwner || isOffice) && hasAddon && hasAddon('phone_system') && (
                <DropdownMenuItem onClick={() => navigate('/settings/phone-numbers')}>
                  <Phone className="w-4 h-4 mr-2" />
                  Phone Numbers
                </DropdownMenuItem>
              )}
              {/* Communications Settings */}
              {(isOwner || isOffice) && hasAddon && hasAddon('phone_system') && (
                <DropdownMenuItem onClick={() => navigate('/settings/communications')}>
                  <Headphones className="w-4 h-4 mr-2" />
                  Communications
                </DropdownMenuItem>
              )}
              {/* Custom Fields Settings */}
              {(isOwner || isOffice) && (
                <DropdownMenuItem onClick={() => navigate('/settings/custom-fields')} data-testid="mobile-custom-fields-menu">
                  <Wrench className="w-4 h-4 mr-2" />
                  Custom Fields
                </DropdownMenuItem>
              )}
              {/* Workflows Settings */}
              {(isOwner || isOffice) && (
                <DropdownMenuItem onClick={() => navigate('/settings/workflows')} data-testid="mobile-workflows-menu">
                  <Zap className="w-4 h-4 mr-2" />
                  Workflows
                </DropdownMenuItem>
              )}
              {/* Job Costing Settings */}
              {(isOwner || isOffice) && (
                <DropdownMenuItem onClick={() => navigate('/settings/job-costing')} data-testid="mobile-job-costing-menu">
                  <DollarSign className="w-4 h-4 mr-2" />
                  Job Costing
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={openSupportDrawer} data-testid="mobile-support-menu-item">
                <Headphones className="w-4 h-4 mr-2" />
                Support
              </DropdownMenuItem>
              <DropdownMenuItem onClick={openFeedbackModal}>
                <MessageSquare className="w-4 h-4 mr-2" />
                Send Feedback
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleLogout} className="text-destructive focus:text-destructive">
                <LogOut className="w-4 h-4 mr-2" />
                {t('auth.logout')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          {/* Hamburger Menu */}
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </Button>
        </div>
      </header>

      {/* Mobile Search Overlay */}
      {mobileSearchOpen && (
        <div className="md:hidden fixed inset-0 z-[60] bg-background">
          <div className="flex items-center h-14 px-4 border-b border-border gap-2">
            <div className="flex-1">
              <GlobalSearch autoFocus onClose={() => setMobileSearchOpen(false)} />
            </div>
            <Button variant="ghost" size="sm" onClick={() => setMobileSearchOpen(false)}>
              Cancel
            </Button>
          </div>
        </div>
      )}

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div className="md:hidden fixed inset-0 z-40 bg-black/50" onClick={() => setMobileMenuOpen(false)}>
          <div className="fixed top-14 right-0 w-64 h-[calc(100%-3.5rem)] bg-background shadow-lg overflow-y-auto" onClick={e => e.stopPropagation()}>
            <nav className="p-4 space-y-2 pb-24">
              {navItems.map((item, index) => {
                /* ─── Generic mobile section handler ─── */
                const mobileSectionConfigs = {
                  'customers-section': { key: 'customers', icon: Users, label: t('nav.customers'), expanded: customersExpanded, toggle: () => { markSectionsInitialized(); setCustomersExpanded(p => !p); }, items: customersSubItems, isActive: location.pathname.startsWith('/customers') || customersSubItems.some(sub => location.pathname.startsWith(sub.to)), navigateTo: '/customers' },
                  'inventory-section': { key: 'inventory', icon: Package, label: t('nav.inventory'), expanded: inventoryExpanded, toggle: () => { markSectionsInitialized(); setInventoryExpanded(p => !p); }, items: inventorySubItems, isActive: inventorySubItems.filter(s => s.to).some(sub => location.pathname.startsWith(sub.to)), filterFn: (sub) => {
                    if (sub.type === 'separator') return true;
                    return !(isTech && sub.to !== '/warehouse');
                  }},
                  'fleet-section': { key: 'fleet', icon: Truck, label: t('nav.fleet'), expanded: fleetExpanded, toggle: () => { markSectionsInitialized(); setFleetExpanded(p => !p); }, items: [
                    ...((isOwner || isOffice) ? [{ to: '/vans', icon: Truck, label: t('nav.vehicles') }] : []),
                    ...fleetSubItems
                  ], isActive: location.pathname.startsWith('/vans') || fleetSubItems.some(sub => location.pathname.startsWith(sub.to)) },
                  'purchasing-section': { key: 'purchasing', icon: ShoppingCart, label: t('nav.pos'), expanded: purchasingExpanded, toggle: () => { markSectionsInitialized(); setPurchasingExpanded(p => !p); }, items: purchasingSubItems, isActive: location.pathname.startsWith('/purchase-orders') || purchasingSubItems.some(sub => location.pathname.startsWith(sub.to)), navigateTo: '/purchase-orders' },
                  'operations-section': { key: 'operations', icon: ClipboardCheck, label: t('nav.operations'), expanded: operationsExpanded, toggle: () => { markSectionsInitialized(); setOperationsExpanded(p => !p); }, items: operationsSubItems, isActive: operationsSubItems.some(sub => location.pathname.startsWith(sub.to)), filterFn: (sub) => {
                    if (sub.permission && !hasPerm(sub.permission)) return false;
                    if (isTech && sub.to !== '/inventory/activity' && sub.to !== '/part-requests') return false;
                    return true;
                  }},
                  'reports-section': { key: 'reports', icon: BarChart3, label: t('nav.reports'), expanded: reportsExpanded, toggle: () => { markSectionsInitialized(); setReportsExpanded(p => !p); }, items: reportsSubItems, isActive: location.pathname.startsWith('/reports') || reportsSubItems.some(sub => location.pathname.startsWith(sub.to)), navigateTo: '/reports', filterFn: (sub) => {
                    if (sub.permission && !hasPerm(sub.permission)) return false;
                    return true;
                  }},
                  'marketing-section': { key: 'marketing', icon: Megaphone, label: t('nav.marketing'), expanded: marketingExpanded, toggle: () => { markSectionsInitialized(); setMarketingExpanded(p => !p); }, items: marketingSubItems, isActive: location.pathname.startsWith('/marketing'), navigateTo: '/marketing/reviews' },
                };

                const mcfg = mobileSectionConfigs[item.type];
                if (mcfg) {
                  const visibleItems = mcfg.filterFn ? mcfg.items.filter(mcfg.filterFn) : mcfg.items;
                  const handleMobileParent = () => {
                    if (mcfg.navigateTo) {
                      navigate(mcfg.navigateTo);
                      setMobileMenuOpen(false);
                      if (!mcfg.expanded) mcfg.toggle();
                    } else {
                      mcfg.toggle();
                    }
                  };
                  const handleMobileChevron = (e) => {
                    e.stopPropagation();
                    mcfg.toggle();
                  };

                  return (
                    <div key={`mobile-${mcfg.key}-section`}>
                      <button
                        onClick={handleMobileParent}
                        className={`w-full flex items-center gap-3 px-4 py-3 rounded-md font-medium transition-colors ${
                          mcfg.isActive && !mcfg.expanded ? 'bg-primary text-primary-foreground' : 'text-foreground hover:bg-muted'
                        }`}
                      >
                        <mcfg.icon className="w-5 h-5" />
                        <span className="flex-1 text-left">{mcfg.label}</span>
                        <span onClick={handleMobileChevron} className="p-1 -m-1 rounded hover:bg-muted/50 transition-colors">
                          {mcfg.expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                        </span>
                      </button>
                      {mcfg.expanded && (
                        <div className="ml-4 mt-1 space-y-1">
                          {visibleItems.map((subItem, subIdx) => {
                            if (subItem.type === 'separator') {
                              return (
                                <div key={`sep-${subIdx}`} className="px-4 pt-2 pb-1">
                                  <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">{subItem.label}</span>
                                </div>
                              );
                            }
                            return (
                              <NavLink
                                key={subItem.to}
                                to={subItem.to}
                                onClick={() => setMobileMenuOpen(false)}
                                className={({ isActive }) =>
                                  `flex items-center gap-3 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                                    isActive ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                                  }`
                                }
                              >
                                <subItem.icon className="w-4 h-4" />
                                <span>{subItem.labelKey ? t(subItem.labelKey) : subItem.label}</span>
                              </NavLink>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                }
                
                // Skip unknown section types to prevent crashes
                if (item.type) return null;
                
                // Check permission if specified
                if (item.permission && !hasPerm(item.permission)) {
                  return null;
                }
                
                // Defensive: skip items with missing icon to prevent React crash
                if (!item.icon || !item.to) return null;
                
                return (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    onClick={() => setMobileMenuOpen(false)}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-4 py-3 rounded-md font-medium transition-colors ${
                        isActive
                          ? 'bg-primary text-primary-foreground'
                          : 'text-foreground hover:bg-muted'
                      }`
                    }
                  >
                    <item.icon className="w-5 h-5" />
                    {item.labelKey ? t(item.labelKey) : item.label}
                  </NavLink>
                );
              })}
            </nav>
            {/* User info moved to top-right dropdown */}
          </div>
        </div>
      )}

      {/* Mobile Bottom Navigation */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 z-50 shadow-[0_-4px_12px_rgba(0,0,0,0.2)] safe-bottom border-t border-white/10" style={{ backgroundColor: 'var(--topbar-bg)' }}>
        <div className="flex items-center justify-around" style={{ height: '60px' }}>
          {[
            { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard', labelKey: 'nav.dashboard' },
            { to: '/customers', icon: Users, label: 'Customers', labelKey: 'nav.customers' },
            { to: '/jobs', icon: Briefcase, label: 'Jobs', labelKey: 'nav.jobs' },
            { to: '/dispatch', icon: CalendarClock, label: 'Dispatch', labelKey: 'nav.dispatch' },
            { to: '/messages', icon: MessageSquare, label: 'Messages', labelKey: 'nav.messages', badge: unreadMessagesCount },
          ].map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                `flex flex-col items-center justify-center flex-1 min-h-[48px] gap-1 relative transition-all duration-200 ${
                  isActive
                    ? 'text-white'
                    : 'text-white/50 active:text-white/70'
                }`
              }
              data-testid={`mobile-nav-${item.label.toLowerCase().replace(' ', '-')}`}
            >
              {({ isActive }) => (
                <>
                  {isActive && (
                    <span className="absolute inset-x-1 inset-y-0 rounded-full bg-white/[0.12]" />
                  )}
                  <div className="relative z-10">
                    <item.icon className={`w-[22px] h-[22px] ${isActive ? 'stroke-[2.4]' : 'stroke-[1.8]'}`} />
                    {item.badge > 0 && (
                      <span className="absolute -top-1.5 -right-3 min-w-[18px] h-[18px] bg-red-500 text-[10px] font-bold text-white rounded-full flex items-center justify-center px-1 ring-2 ring-[var(--topbar-bg)]">
                        {item.badge > 99 ? '99+' : item.badge}
                      </span>
                    )}
                  </div>
                  <span className={`relative z-10 text-[11px] ${isActive ? 'font-semibold' : 'font-medium'}`}>{item.labelKey ? t(item.labelKey) : item.label}</span>
                </>
              )}
            </NavLink>
          ))}
        </div>
      </nav>

      {/* Main Content - offset for header and sidebar */}
      <main className={`pt-14 pb-20 md:pb-0 min-h-screen transition-[padding-left,padding-bottom] duration-200 ease-in-out ${sidebarCollapsed ? 'md:pl-16' : 'md:pl-64'} ${activeCallBarVisible ? 'md:pb-14' : ''}`}>
        {/* Trial bar - sticky below top bar, full width */}
        <TrialCountdownBanner />
        <div className="p-4 md:p-8">
          {/* Onboarding Banners - Non-blocking welcome & setup nudges */}
          <OnboardingBanners />
          {/* Billing Banners - Beta Sunset only (trial moved above) */}
          <BetaSunsetBanner />
          {children}
        </div>
      </main>

      {/* Feedback Modal */}
      {feedbackOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50" onClick={() => setFeedbackOpen(false)}>
          <div className="bg-background rounded-lg shadow-xl w-full max-w-md mx-4 p-6" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold font-heading">Send Feedback</h2>
              <Button variant="ghost" size="icon" onClick={() => setFeedbackOpen(false)}>
                <X className="w-4 h-4" />
              </Button>
            </div>
            <p className="text-sm text-muted-foreground mb-4">
              Help us improve! Share your thoughts, report issues, or suggest features.
            </p>
            <form onSubmit={handleFeedbackSubmit}>
              <textarea
                className="w-full h-32 p-3 border border-border rounded-md text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="What's on your mind?"
                value={feedbackMessage}
                onChange={(e) => setFeedbackMessage(e.target.value)}
                required
                maxLength={2000}
                data-testid="feedback-message"
              />
              <input
                type="email"
                className="w-full mt-3 p-3 border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="Email (optional)"
                value={feedbackEmail}
                onChange={(e) => setFeedbackEmail(e.target.value)}
                data-testid="feedback-email"
              />
              <div className="flex justify-end gap-2 mt-4">
                <Button type="button" variant="outline" onClick={() => setFeedbackOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={submitting || !feedbackMessage.trim()} data-testid="feedback-submit">
                  {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  Submit
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Global Scanner (from mobile header) */}
      <ScanManager 
        open={scannerOpen}
        onClose={() => setScannerOpen(false)}
        context={location.pathname.includes('/vans/') ? 'vehicle' : 'warehouse'}
        vehicleId={location.pathname.includes('/vans/') ? location.pathname.split('/vans/')[1] : null}
      />

      {/* Guided Tour */}
      <TourRunner tourId="activation" />
    </div>
  );
};
;
